/**
 * ContentEditor Component
 * Provides editing UI for sentences, vocabulary, phrases, and quiz questions
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import creatorWorkflowService from '../../services/creatorWorkflowService';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import type { CourseDraft, AnnotatedSentence, ComprehensiveVocabularyEntry } from '../../types/creator-workflow';
import './ContentEditor.css';

// ============ Types ============
interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onSave: () => Promise<void>;
  saving: boolean;
}

interface SentenceEditorProps {
  draftId: string;
  sentence: AnnotatedSentence;
  index: number;
  onUpdate: (draft: CourseDraft) => void;
  onClose: () => void;
}

interface VocabularyEditorProps {
  draftId: string;
  vocab: ComprehensiveVocabularyEntry;
  index: number;
  onUpdate: (draft: CourseDraft) => void;
  onClose: () => void;
}

interface PhraseEditorProps {
  draftId: string;
  phrase: any;
  index: number;
  onUpdate: (draft: CourseDraft) => void;
  onClose: () => void;
}

interface QuizEditorProps {
  draftId: string;
  question: any;
  index: number;
  onUpdate: (draft: CourseDraft) => void;
  onClose: () => void;
}

// ============ Edit Modal ============
const EditModal: React.FC<EditModalProps> = ({ isOpen, onClose, title, children, onSave, saving }) => {
  const { t } = useTranslation(['creator', 'common']);
  if (!isOpen) return null;

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h3>{title}</h3>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>
        <div className="edit-modal-content">
          {children}
        </div>
        <div className="edit-modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>
            {t('common:cancel')}
          </button>
          <button className="btn-save" onClick={onSave} disabled={saving}>
            {saving ? t('common:saving') : t('common:save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Sentence Editor ============
export const SentenceEditor: React.FC<SentenceEditorProps> = ({
  draftId,
  sentence,
  index,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [text, setText] = useState(sentence.text || '');
  const [translationZhTw, setTranslationZhTw] = useState(sentence.translation || '');
  const [speaker, setSpeaker] = useState(sentence.speaker || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updatedDraft = await creatorWorkflowService.updateSentence(draftId, index, {
        text,
        translation: translationZhTw,
        speaker: speaker || null,
      });
      onUpdate(updatedDraft);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditModal
      isOpen={true}
      onClose={onClose}
      title={t('creator:workflow.contentEditor.editSentence', { index: index + 1 })}
      onSave={handleSave}
      saving={saving}
    >
      {error && <div className="edit-error">{error}</div>}

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.englishText')}</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={t('creator:workflow.contentEditor.englishPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.chineseTranslation')}</label>
        <textarea
          value={translationZhTw}
          onChange={(e) => setTranslationZhTw(e.target.value)}
          rows={3}
          placeholder={t('creator:workflow.contentEditor.chinesePlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.speaker')}</label>
        <input
          type="text"
          value={speaker}
          onChange={(e) => setSpeaker(e.target.value)}
          placeholder={t('creator:workflow.contentEditor.speakerPlaceholder')}
        />
      </div>
    </EditModal>
  );
};

// ============ Vocabulary Editor ============
export const VocabularyEditor: React.FC<VocabularyEditorProps> = ({
  draftId,
  vocab,
  index,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [word, setWord] = useState(vocab.word || '');
  const [definitionInContext, setDefinitionInContext] = useState(vocab.definition_in_context || '');
  const [context, setContext] = useState(vocab.context || '');
  const [pos, setPos] = useState<'noun' | 'verb' | 'adjective' | 'adverb' | 'other'>(vocab.pos || 'other');
  const [difficulty, setDifficulty] = useState(vocab.difficulty || 5);
  const [frequency, setFrequency] = useState<'common' | 'uncommon' | 'rare'>(vocab.frequency || 'common');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updatedDraft = await creatorWorkflowService.updateVocabulary(draftId, index, {
        word,
        definition_in_context: definitionInContext,
        context,
        pos,
        difficulty,
        frequency,
      });
      onUpdate(updatedDraft);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditModal
      isOpen={true}
      onClose={onClose}
      title={t('creator:workflow.contentEditor.editWord', { word: vocab.word })}
      onSave={handleSave}
      saving={saving}
    >
      {error && <div className="edit-error">{error}</div>}

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.word')}</label>
        <input
          type="text"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          placeholder={t('creator:workflow.contentEditor.wordPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.contextDefinition')}</label>
        <textarea
          value={definitionInContext}
          onChange={(e) => setDefinitionInContext(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.contextDefinitionPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.contextSentence')}</label>
        <textarea
          value={context}
          onChange={(e) => setContext(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.contextSentencePlaceholder')}
        />
      </div>

      <div className="edit-row">
        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.partOfSpeech')}</label>
          <select value={pos} onChange={(e) => setPos(e.target.value as typeof pos)}>
            <option value="noun">{t('creator:workflow.contentEditor.posNoun')}</option>
            <option value="verb">{t('creator:workflow.contentEditor.posVerb')}</option>
            <option value="adjective">{t('creator:workflow.contentEditor.posAdjective')}</option>
            <option value="adverb">{t('creator:workflow.contentEditor.posAdverb')}</option>
            <option value="other">{t('creator:workflow.contentEditor.posOther')}</option>
          </select>
        </div>

        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.difficulty')}</label>
          <input
            type="number"
            min="1"
            max="10"
            value={difficulty}
            onChange={(e) => setDifficulty(parseInt(e.target.value) || 5)}
          />
        </div>

        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.frequency')}</label>
          <select value={frequency} onChange={(e) => setFrequency(e.target.value as typeof frequency)}>
            <option value="common">{t('creator:workflow.contentEditor.freqCommon')}</option>
            <option value="uncommon">{t('creator:workflow.contentEditor.freqUncommon')}</option>
            <option value="rare">{t('creator:workflow.contentEditor.freqRare')}</option>
          </select>
        </div>
      </div>
    </EditModal>
  );
};

// ============ Phrase Editor ============
export const PhraseEditor: React.FC<PhraseEditorProps> = ({
  draftId,
  phrase,
  index,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [text, setText] = useState(phrase.text || '');
  const [type, setType] = useState(phrase.type || 'collocation');
  const [definitionEn, setDefinitionEn] = useState(phrase.definition_en || '');
  const [definitionZhTw, setDefinitionZhTw] = useState(phrase.definition_native || '');
  const [explanationZhTw, setExplanationZhTw] = useState(phrase.explanation_native || '');
  const [origin, setOrigin] = useState(phrase.origin || '');
  const [usageExample, setUsageExample] = useState(phrase.usage_example || '');
  const [usageSituation, setUsageSituation] = useState(phrase.usage_situation || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updatedDraft = await creatorWorkflowService.updatePhrase(draftId, index, {
        text,
        type,
        definition_en: definitionEn,
        definition_native: definitionZhTw,
        explanation_native: explanationZhTw,
        origin,
        usage_example: usageExample,
        usage_situation: usageSituation,
      });
      onUpdate(updatedDraft);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditModal
      isOpen={true}
      onClose={onClose}
      title={t('creator:workflow.contentEditor.editPhrase', { text: phrase.text })}
      onSave={handleSave}
      saving={saving}
    >
      {error && <div className="edit-error">{error}</div>}

      <div className="edit-row">
        <div className="edit-field flex-2">
          <label>{t('creator:workflow.contentEditor.phrase')}</label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('creator:workflow.contentEditor.phrasePlaceholder')}
          />
        </div>

        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.type')}</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="idiom">{t('creator:workflow.contentEditor.typeIdiom')}</option>
            <option value="phrasal_verb">{t('creator:workflow.contentEditor.typePhrasalVerb')}</option>
            <option value="collocation">{t('creator:workflow.contentEditor.typeCollocation')}</option>
          </select>
        </div>
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.definitionEn')}</label>
        <textarea
          value={definitionEn}
          onChange={(e) => setDefinitionEn(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.definitionEnPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.definitionZh')}</label>
        <textarea
          value={definitionZhTw}
          onChange={(e) => setDefinitionZhTw(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.definitionZhPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.explanationZh')}</label>
        <textarea
          value={explanationZhTw}
          onChange={(e) => setExplanationZhTw(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.explanationZhPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.origin')}</label>
        <input
          type="text"
          value={origin}
          onChange={(e) => setOrigin(e.target.value)}
          placeholder={t('creator:workflow.contentEditor.originPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.usageExample')}</label>
        <textarea
          value={usageExample}
          onChange={(e) => setUsageExample(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.usageExamplePlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.usageSituation')}</label>
        <input
          type="text"
          value={usageSituation}
          onChange={(e) => setUsageSituation(e.target.value)}
          placeholder={t('creator:workflow.contentEditor.usageSituationPlaceholder')}
        />
      </div>
    </EditModal>
  );
};

// ============ Quiz Question Editor ============
export const QuizEditor: React.FC<QuizEditorProps> = ({
  draftId,
  question,
  index,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [type, setType] = useState(question.type || 'multiple_choice');
  const [questionEn, setQuestionEn] = useState(question.question_en || '');
  const [questionZhTw, setQuestionZhTw] = useState(question.question_native || '');
  const [options, setOptions] = useState<Array<{ text: string; translation: string }>>(
    question.options || [{ text: '', translation: '' }]
  );
  const [correctAnswer, setCorrectAnswer] = useState<string>(
    Array.isArray(question.correct_answer) ? question.correct_answer[0] : question.correct_answer || ''
  );
  const [explanationEn, setExplanationEn] = useState(question.explanation_en || '');
  const [explanationZhTw, setExplanationZhTw] = useState(question.explanation_native || '');
  const [difficulty, setDifficulty] = useState(question.difficulty || 'intermediate');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddOption = () => {
    setOptions([...options, { text: '', translation: '' }]);
  };

  const handleRemoveOption = (idx: number) => {
    setOptions(options.filter((_, i) => i !== idx));
  };

  const handleOptionChange = (idx: number, field: 'text' | 'translation', value: string) => {
    const newOptions = [...options];
    newOptions[idx][field] = value;
    setOptions(newOptions);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updatedDraft = await creatorWorkflowService.updateQuizQuestion(draftId, index, {
        type,
        question_en: questionEn,
        question_native: questionZhTw,
        options: type === 'multiple_choice' ? options : undefined,
        correct_answer: correctAnswer,
        explanation_en: explanationEn,
        explanation_native: explanationZhTw,
        difficulty,
      });
      onUpdate(updatedDraft);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditModal
      isOpen={true}
      onClose={onClose}
      title={t('creator:workflow.contentEditor.editQuiz', { index: index + 1 })}
      onSave={handleSave}
      saving={saving}
    >
      {error && <div className="edit-error">{error}</div>}

      <div className="edit-row">
        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.questionType')}</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="multiple_choice">{t('creator:workflow.contentEditor.typeMultipleChoice')}</option>
            <option value="fill_blank">{t('creator:workflow.contentEditor.typeFillBlank')}</option>
            <option value="true_false">{t('creator:workflow.contentEditor.typeTrueFalse')}</option>
          </select>
        </div>

        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.quizDifficulty')}</label>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
            <option value="beginner">{t('creator:workflow.contentEditor.diffBeginner')}</option>
            <option value="intermediate">{t('creator:workflow.contentEditor.diffIntermediate')}</option>
            <option value="advanced">{t('creator:workflow.contentEditor.diffAdvanced')}</option>
          </select>
        </div>
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.questionEn')}</label>
        <textarea
          value={questionEn}
          onChange={(e) => setQuestionEn(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.questionEnPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.questionZh')}</label>
        <textarea
          value={questionZhTw}
          onChange={(e) => setQuestionZhTw(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.questionZhPlaceholder')}
        />
      </div>

      {type === 'multiple_choice' && (
        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.options')}</label>
          <div className="options-list">
            {options.map((opt, idx) => (
              <div key={idx} className="option-row">
                <span className="option-label">{String.fromCharCode(65 + idx)}.</span>
                <input
                  type="text"
                  value={opt.text}
                  onChange={(e) => handleOptionChange(idx, 'text', e.target.value)}
                  placeholder={t('creator:workflow.contentEditor.englishOption')}
                  className="option-input"
                />
                <input
                  type="text"
                  value={opt.translation}
                  onChange={(e) => handleOptionChange(idx, 'translation', e.target.value)}
                  placeholder={t('creator:workflow.contentEditor.chineseOption')}
                  className="option-input"
                />
                <input
                  type="radio"
                  name="correctAnswer"
                  checked={correctAnswer === opt.text}
                  onChange={() => setCorrectAnswer(opt.text)}
                  title={t('creator:workflow.contentEditor.setCorrectAnswer')}
                />
                {options.length > 2 && (
                  <button
                    type="button"
                    className="btn-remove-option"
                    onClick={() => handleRemoveOption(idx)}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            {options.length < 6 && (
              <button type="button" className="btn-add-option" onClick={handleAddOption}>
                {t('creator:workflow.contentEditor.addOption')}
              </button>
            )}
          </div>
        </div>
      )}

      {type !== 'multiple_choice' && (
        <div className="edit-field">
          <label>{t('creator:workflow.contentEditor.correctAnswer')}</label>
          <input
            type="text"
            value={correctAnswer}
            onChange={(e) => setCorrectAnswer(e.target.value)}
            placeholder={t('creator:workflow.contentEditor.correctAnswerPlaceholder')}
          />
        </div>
      )}

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.explanationEnLabel')}</label>
        <textarea
          value={explanationEn}
          onChange={(e) => setExplanationEn(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.explanationEnPlaceholder')}
        />
      </div>

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.explanationZhLabel')}</label>
        <textarea
          value={explanationZhTw}
          onChange={(e) => setExplanationZhTw(e.target.value)}
          rows={2}
          placeholder={t('creator:workflow.contentEditor.explanationZhPlaceholder2')}
        />
      </div>
    </EditModal>
  );
};

// ============ Sentence Analysis Editor ============
interface SentenceAnalysisEditorProps {
  draftId: string;
  sentence: AnnotatedSentence;
  index: number;
  onUpdate: (draft: CourseDraft) => void;
  onClose: () => void;
}

export const SentenceAnalysisEditor: React.FC<SentenceAnalysisEditorProps> = ({
  draftId,
  sentence,
  index,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const analysis = sentence.sentence_analysis;
  const [explanationZhTw, setExplanationZhTw] = useState(analysis?.explanation_native || '');
  const [annotations, setAnnotations] = useState<Array<{ text: string; startTime: number; endTime: number }>>(
    analysis?.annotations?.map((a: any) => ({
      text: a.text || '',
      startTime: a.startTime || 0,
      endTime: a.endTime || 0,
    })) || []
  );
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Extract just the text for annotations
      const annotationTexts = annotations.map(a => a.text);
      await creatorWorkflowService.updateSentenceAnalysis(draftId, index, {
        explanation_native: explanationZhTw,
        annotations: annotationTexts,
      });
      // Refresh draft data
      const updatedDraft = await creatorWorkflowService.getDraft(draftId);
      onUpdate(updatedDraft);
      setSuccess(t('creator:workflow.contentEditor.saved'));
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateAudio = async () => {
    setRegenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await creatorWorkflowService.regenerateSentenceExplanationAudio(draftId, index);
      // Update local annotations with new timestamps
      if (result.adjustedAnnotations && Array.isArray(result.adjustedAnnotations)) {
        // The adjustedAnnotations from backend contains full annotation objects
        setAnnotations(result.adjustedAnnotations.map((a: any) => ({
          text: a.text || '',
          startTime: a.startTime || 0,
          endTime: a.endTime || 0,
        })));
      }
      // Refresh draft data to get new audio URL
      const updatedDraft = await creatorWorkflowService.getDraft(draftId);
      onUpdate(updatedDraft);
      setSuccess(t('creator:workflow.contentEditor.audioRegenerated', { duration: result.duration.toFixed(2) }));
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate audio');
    } finally {
      setRegenerating(false);
    }
  };

  const handleAnnotationChange = (idx: number, field: 'text' | 'startTime' | 'endTime', value: string | number) => {
    const newAnnotations = [...annotations];
    if (field === 'text') {
      newAnnotations[idx].text = value as string;
    } else if (field === 'startTime') {
      newAnnotations[idx].startTime = typeof value === 'string' ? parseFloat(value) || 0 : value;
    } else {
      newAnnotations[idx].endTime = typeof value === 'string' ? parseFloat(value) || 0 : value;
    }
    setAnnotations(newAnnotations);
  };

  const handleAddAnnotation = () => {
    const lastAnnotation = annotations[annotations.length - 1];
    const newStart = lastAnnotation ? lastAnnotation.endTime : 0;
    setAnnotations([...annotations, { text: '', startTime: newStart, endTime: newStart + 1 }]);
  };

  const handleRemoveAnnotation = (idx: number) => {
    setAnnotations(annotations.filter((_, i) => i !== idx));
  };

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal sentence-analysis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h3>{t('creator:workflow.contentEditor.editExplanation', { index: index + 1 })}</h3>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>
        <div className="edit-modal-content">
          {error && <div className="edit-error">{error}</div>}
          {success && <div className="edit-success">{success}</div>}

          <div className="sentence-preview">
            <label>{t('creator:workflow.contentEditor.originalSentence')}</label>
            <p className="preview-text">{sentence.text}</p>
          </div>

          <div className="edit-field">
            <label>{t('creator:workflow.contentEditor.chineseExplanation')}</label>
            <textarea
              value={explanationZhTw}
              onChange={(e) => setExplanationZhTw(e.target.value)}
              rows={6}
              placeholder={t('creator:workflow.contentEditor.explanationPlaceholder')}
            />
            <p className="field-hint">{t('creator:workflow.contentEditor.explanationHint')}</p>
          </div>

          <div className="edit-field">
            <label>{t('creator:workflow.contentEditor.audioTimestamps', { count: annotations.length })}</label>
            <div className="annotations-list">
              {annotations.map((ann, idx) => (
                <div key={idx} className="annotation-row">
                  <span className="annotation-number">{idx + 1}.</span>
                  <input
                    type="text"
                    value={ann.text}
                    onChange={(e) => handleAnnotationChange(idx, 'text', e.target.value)}
                    placeholder={t('creator:workflow.contentEditor.segmentText')}
                    className="annotation-text"
                  />
                  <div className="annotation-times">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={ann.startTime}
                      onChange={(e) => handleAnnotationChange(idx, 'startTime', e.target.value)}
                      className="annotation-time"
                      title={t('creator:workflow.contentEditor.startTime')}
                    />
                    <span>-</span>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={ann.endTime}
                      onChange={(e) => handleAnnotationChange(idx, 'endTime', e.target.value)}
                      className="annotation-time"
                      title={t('creator:workflow.contentEditor.endTime')}
                    />
                    <span className="time-unit">{t('creator:workflow.contentEditor.seconds')}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-annotation"
                    onClick={() => handleRemoveAnnotation(idx)}
                    title={t('creator:workflow.contentEditor.removeSegment')}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button type="button" className="btn-add-annotation" onClick={handleAddAnnotation}>
                {t('creator:workflow.contentEditor.addSegment')}
              </button>
            </div>
          </div>

          {analysis?.explanation_audio_url && (
            <div className="current-audio-preview">
              <label>{t('creator:workflow.contentEditor.currentAudio')}</label>
              <audio
                controls
                src={resolveMediaUrl(analysis.explanation_audio_url)}
              />
            </div>
          )}
        </div>
        <div className="edit-modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={saving || regenerating}>
            {t('common:cancel')}
          </button>
          <button
            className="btn-regenerate"
            onClick={handleRegenerateAudio}
            disabled={saving || regenerating}
            title={t('creator:workflow.contentEditor.regenerateAudioTitle')}
          >
            {regenerating ? t('creator:workflow.contentEditor.regeneratingAudio') : t('creator:workflow.contentEditor.regenerateAudioBtn')}
          </button>
          <button className="btn-save" onClick={handleSave} disabled={saving || regenerating}>
            {saving ? t('common:saving') : t('common:save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Full Text Translation Editor ============
interface FullTextTranslationEditorProps {
  draftId: string;
  currentTranslation: string;
  onUpdate: (draft: CourseDraft) => void;
  onClose: () => void;
}

export const FullTextTranslationEditor: React.FC<FullTextTranslationEditorProps> = ({
  draftId,
  currentTranslation,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [translation, setTranslation] = useState(currentTranslation || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updatedDraft = await creatorWorkflowService.updateFullTextTranslation(draftId, {
        translation: translation,
      });
      onUpdate(updatedDraft);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditModal
      isOpen={true}
      onClose={onClose}
      title={t('creator:workflow.contentEditor.editTranslation')}
      onSave={handleSave}
      saving={saving}
    >
      {error && <div className="edit-error">{error}</div>}

      <div className="edit-field">
        <label>{t('creator:workflow.contentEditor.fullTranslation')}</label>
        <textarea
          value={translation}
          onChange={(e) => setTranslation(e.target.value)}
          rows={12}
          placeholder={t('creator:workflow.contentEditor.translationPlaceholder')}
        />
      </div>
    </EditModal>
  );
};
